'use no memo';
import React, { useCallback, useState } from 'react';
import { css } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import {
	useCommentsDataState,
	type CommentData,
	type ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import type { PageInfoNode } from '@confluence/page-info/entry-points/PageInfoQuery.ts';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { useCommentsPanelState } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import type { ViewReplyOptions } from '@confluence/comments-common/entry-points/repliesHelpers.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { formatTimestampForAriaLabel } from '@confluence/comments-util/entry-points/dateUtils.ts';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';

import { ObjectComment } from './ObjectComment.tsx';
import { ObjectCommentReplyEditor } from './ObjectCommentReplyEditor.tsx';

const replyBoxStyles = css({
	listStyle: 'none',
	position: 'relative',
	paddingLeft: token('space.300'),
});

const replyBoxStylesV2 = css({
	paddingLeft: token('space.200'),
});

const objectCommentWrapperStyles = css({
	paddingBottom: token('space.100'),
});

const nestedReplyThreadBoxStyles = css({
	display: 'flex',
	flexDirection: 'column',
	marginLeft: token('space.200'), // margin vs padding to get border to align correctly when present
});

const nestedReplyThreadBoxStylesV2 = css({
	paddingLeft: token('space.250'),
});

const topLevelReplyThreadBoxStyles = css({
	display: 'flex',
	flexDirection: 'column',
});

const withLeftBorderStyle = css({
	borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const withLeftBorderStyleUpdated = css({
	borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const withLeftBoxShadow = css({
	boxShadow: `inset 0.08rem 0 0 0 ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const footerReplyContainerStyle = css({
	position: 'relative',
	'&::before': {
		content: "''",
		position: 'absolute',
		left: '0px',
		top: '0px',
		bottom: '0px',
		borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	},
});

const footerReplyContainerStyleUpdated = css({
	'&::before': {
		borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	},
});

// Curved branch styles for connecting replies
const replyBranchStyles = css({
	position: 'relative',
	'&::before': {
		content: "''",
		position: 'absolute',
		left: '0px', // Align with the border position
		top: '0px',
		width: '12.3px',
		height: '24px',
		borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		borderBottom: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		borderBottomLeftRadius: token('radius.large'),
		boxSizing: 'border-box',
	},
});

const replyBranchStylesUpdated = css({
	'&::before': {
		borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		borderBottom: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	},
});

const i18n = defineMessages({
	replyAriaLabel: {
		id: 'object-comments.reply.aria-label',
		defaultMessage: 'Reply by {authorName}, {timestamp}',
		description: 'Aria label for reply container announcing the author',
	},
});

export const ObjectCommentNestableReplies = ({
	parentReplyId,
	parentToRepliesMap,
	handleMouseEnter,
	handleMouseLeave,
	canAddComments,
	pageInfo,
	pageMode,
	threadKey,
	supportedTopLevelActions,
	hoveredCommentId,
	isTopLevelReply,
	parentComment,
	parentReplyButtonAllowed,
	setReplyView,
	isOffline,
	flags,
	isEditorExpanded,
}: {
	parentReplyId: string;
	parentToRepliesMap: { [key: string]: ReplyData[] };
	handleMouseEnter: (id?: string) => void;
	handleMouseLeave: () => void;
	canAddComments: boolean;
	pageInfo: PageInfoNode | null;
	pageMode: PageMode;
	threadKey: string;
	supportedTopLevelActions: CommentAction[];
	hoveredCommentId: string;
	isTopLevelReply: boolean;
	parentComment: CommentData;
	parentReplyButtonAllowed: boolean;
	setReplyView: (view: ViewReplyOptions) => void;
	isOffline?: boolean;
	flags?: FlagsStateContainer;
	isEditorExpanded: boolean;
}) => {
	const { replyToCommentId, location, footerEditCommentIds } = useCommentsPanelState();
	const { hasNestedComments: legacyHasNestedComments } = useCommentsDataState();
	const processedCommentsData = useProcessedCommentsDataState();

	const hasNestedComments = expValEquals('cc_comments_new_comments_data_manager', 'isEnabled', true)
		? processedCommentsData.hasNestedComments
		: legacyHasNestedComments;

	const [commentIsFocused, setCommentIsFocused] = useState<string | null>(null);
	const intl = useIntl();
	const { formatMessage } = intl;
	const { unreadCommentIDSet } = useUnreadCommentsState();

	const isParentCommentOpen = parentComment.isOpen;

	const repliesToRender = parentToRepliesMap[parentReplyId];

	const handleFocus = useCallback(
		(event: React.FocusEvent, replyId: string) => {
			event.stopPropagation();
			setCommentIsFocused(replyId);
		},
		[setCommentIsFocused],
	);

	const handleBlur = useCallback(
		(event: React.FocusEvent) => {
			// Only clear focus if we're not focusing a child element
			if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget as Node)) {
				setCommentIsFocused(null);
			}
		},
		[setCommentIsFocused],
	);

	return (
		<div
			data-testId="footer-reply-container"
			role="list"
			css={[
				isTopLevelReply ? topLevelReplyThreadBoxStyles : nestedReplyThreadBoxStyles,
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				fg('confluence-fe-footer-comment-left-border') &&
					isTopLevelReply &&
					!expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
					footerReplyContainerStyle,
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				fg('confluence-fe-footer-comment-left-border') &&
					isTopLevelReply &&
					!expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
					fg('confluence_comment_border_migration') &&
					footerReplyContainerStyleUpdated,
			]}
		>
			{repliesToRender.map((reply, idx) => {
				// These all control the visibility of the reply editor and left border
				// Generally speaking, we want to show the left border at the thread level rather than from comment body (showLeftBorder)
				// However, if one of the comment's will not have a left border (for example, if it's the last reply), we need to show at the comment level (!hideBranchingStyle)
				const replyHasChildren = parentToRepliesMap[reply.id]?.length > 0;

				const isLastReply = idx === repliesToRender.length - 1;

				const isFooterReplyMatchingReplyToCommentParameter =
					reply.id === replyToCommentId && location === LocationValue.FOOTER;

				const showReplyEditor = expValEquals(
					'confluence_frontend_prevent_new_nested_comments',
					'isEnabled',
					true,
				)
					? hasNestedComments && isFooterReplyMatchingReplyToCommentParameter
					: isFooterReplyMatchingReplyToCommentParameter;

				const parentReplyButtonVisible = parentReplyButtonAllowed && isTopLevelReply;

				const replyHasNestedObjects = replyHasChildren || showReplyEditor;

				const replyIsBeingEdited = footerEditCommentIds?.has(reply.id);

				const showLeftBorder =
					parentReplyButtonVisible ||
					(replyHasNestedObjects && !isLastReply) ||
					(replyIsBeingEdited && !isLastReply);

				const hideBranchingStyle = expValEquals('confluence_comments_tuneup', 'isEnabled', true)
					? isLastReply && (!isTopLevelReply || !isEditorExpanded)
					: parentReplyButtonVisible ||
						replyHasNestedObjects ||
						(!parentReplyButtonVisible && isLastReply);

				// This moves the branching style 1px to the left to account for the 1px left border of the thread
				const adjustForFooterNestedReplies =
					hideBranchingStyle && (!isLastReply || parentReplyButtonVisible);

				// This accounts for the 1px missing border offset of the last reply in a deeply nested thread
				const isLastNestedFooterReply = isLastReply && !isTopLevelReply;

				return (
					<div
						css={[
							replyBoxStyles,
							expValEquals('confluence_comments_tuneup', 'isEnabled', true) && replyBoxStylesV2,
							// Apply curved branch styles for non-last replies
							fg('confluence-fe-footer-comment-left-border') && replyBranchStyles,
							// eslint-disable-next-line confluence-feature-gating/no-preconditioning
							fg('confluence-fe-footer-comment-left-border') &&
								fg('confluence_comment_border_migration') &&
								replyBranchStylesUpdated,
							// Apply visible border when needed
							showLeftBorder &&
								!fg('confluence-fe-footer-comment-left-border') &&
								withLeftBorderStyle,
							showLeftBorder &&
								!fg('confluence-fe-footer-comment-left-border') &&
								fg('confluence_comment_border_migration') &&
								withLeftBorderStyleUpdated,
							showLeftBorder &&
								replyHasNestedObjects &&
								(!isLastReply ||
									(isLastReply && isEditorExpanded) ||
									!expValEquals('confluence_comments_tuneup', 'isEnabled', true)) &&
								fg('confluence_comment_nested_footer_border') &&
								withLeftBoxShadow,
							replyIsBeingEdited &&
								!isLastReply &&
								expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
								withLeftBoxShadow,
						]}
						key={reply.id}
						id={`${LocationValue.FOOTER}-comment-thread-${reply.id}-replies`}
						data-testid={`comment-thread-${parentReplyId}-replies-${reply.id}`}
						onMouseEnter={() => handleMouseEnter(reply.id)}
						onMouseLeave={handleMouseLeave}
						onFocus={(event) => handleFocus(event, reply.id)}
						onBlur={handleBlur}
						role="listitem"
						aria-label={formatMessage(i18n.replyAriaLabel, {
							authorName: reply.author?.displayName || formatMessage(commentsi18n.anonymous),
							timestamp: formatTimestampForAriaLabel({
								intl,
								dateString: reply.createdAtNonLocalized,
								isReply: true,
							}),
						})}
					>
						<div css={[!replyIsBeingEdited && objectCommentWrapperStyles]}>
							{/* The editor has its own bottom padding to help with the left border */}
							<ObjectComment
								comment={reply}
								supportedTopLevelActions={supportedTopLevelActions}
								pageInfo={pageInfo}
								pageMode={pageMode}
								isUnread={unreadCommentIDSet.has(reply.id)}
								isHovered={
									hoveredCommentId === reply.id ||
									(commentIsFocused === reply.id &&
										expVal('confluence_frontend_comments_a11y_kb_exp', 'isEnabled', false))
								}
								threadKey={threadKey}
								// If we have the left border, don't show the branching style
								hideBranchingStyle={hideBranchingStyle}
								adjustForNestedReplies={false}
								canAddComments={canAddComments}
								isParentCommentOpen={isParentCommentOpen}
								adjustForFooterNestedReplies={
									adjustForFooterNestedReplies && !fg('confluence-fe-footer-comment-left-border')
								}
								topLevelParentCommentId={parentComment.id}
								commentHasChildren={replyHasChildren}
								isLastNestedFooterReply={isLastNestedFooterReply}
								flags={flags}
								showRepliesWithCurvedBorders={
									fg('confluence-fe-footer-comment-left-border') ? false : undefined
								}
								replyHasNestedObjects={replyHasNestedObjects}
							/>
						</div>
						{showReplyEditor && (
							<div
								css={[
									nestedReplyThreadBoxStyles,
									replyHasChildren && withLeftBorderStyle,
									replyHasChildren &&
										fg('confluence_comment_border_migration') &&
										withLeftBorderStyleUpdated,
									replyHasChildren &&
										expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
										nestedReplyThreadBoxStylesV2,
								]}
							>
								<ObjectCommentReplyEditor
									pageInfo={pageInfo}
									pageMode={pageMode}
									setReplyView={setReplyView}
									parentComment={parentComment}
									threadKey={threadKey}
									replyToId={reply.id}
									isOffline={isOffline}
									customBeforeLeftForNestedReply={adjustForFooterNestedReplies}
								/>
							</div>
						)}
						{parentToRepliesMap[reply.id]?.length > 0 && (
							<ObjectCommentNestableReplies
								parentReplyId={reply.id}
								parentToRepliesMap={parentToRepliesMap}
								handleMouseEnter={handleMouseEnter}
								handleMouseLeave={handleMouseLeave}
								canAddComments={canAddComments}
								pageInfo={pageInfo}
								pageMode={pageMode}
								threadKey={threadKey}
								supportedTopLevelActions={supportedTopLevelActions}
								hoveredCommentId={hoveredCommentId}
								isTopLevelReply={false}
								parentComment={parentComment}
								parentReplyButtonAllowed={false}
								setReplyView={setReplyView}
								isOffline={isOffline}
								flags={flags}
								isEditorExpanded={isEditorExpanded}
							/>
						)}
					</div>
				);
			})}
		</div>
	);
};
